/**
 * Auth store — single source of truth for the Supabase session.
 *
 * On init, subscribes to supabase.auth.onAuthStateChange and mirrors the
 * session into Zustand state. As a side effect, it also injects the current
 * JWT into the backend API client so authenticated requests carry the token.
 *
 * `isLoading` stays true until the first auth event resolves (either the
 * rehydrated session from localStorage or a confirmed "no session"). Route
 * guards should render a spinner while it's true, not redirect — otherwise
 * a page refresh with an active session briefly flashes /login.
 */

import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { apiClient } from '../lib/api';

type AuthState = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  init: () => () => void;
  signOut: () => Promise<void>;
};

// React StrictMode mounts effects twice in development. We only want a single
// onAuthStateChange subscription and a single getSession() call in flight.
let initialized = false;
let cleanup: (() => void) | null = null;

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  user: null,
  isLoading: true,

  init: () => {
    if (initialized && cleanup) return cleanup;
    initialized = true;

    const applySession = (session: Session | null) => {
      apiClient.setAuthToken(session?.access_token ?? null);
      set({ session, user: session?.user ?? null, isLoading: false });
    };

    // If storage/network fails, fall back to "no session" so the app doesn't
    // get stuck on the loading spinner forever.
    supabase.auth
      .getSession()
      .then(({ data }) => applySession(data.session))
      .catch(() => applySession(null));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
    });

    cleanup = () => {
      subscription.unsubscribe();
      initialized = false;
      cleanup = null;
    };
    return cleanup;
  },

  signOut: async () => {
    await supabase.auth.signOut();
  },
}));
